import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface ClickableCardProps {
  title: string;
  description?: string;
  onClick?: () => void;
  headerContent?: React.ReactNode;
}

export default function ClickableCard({
  title,
  description,
  onClick,
  headerContent,
}: ClickableCardProps) {
  const theme = useTheme();
  const semanticTheme = theme.palette.semantic;
  const cardSx = {
    minHeight: 240,
    width: 360,
    border:
      theme.palette.mode === 'dark'
        ? `0.5px solid ${semanticTheme.outline.light}`
        : undefined,
    backgroundColor: semanticTheme.surface.bg1,
    overflow: 'hidden',
    boxShadow: `1px 2px 4px 0 ${semanticTheme.boxShadow}`,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: `0 4px 12px ${semanticTheme.boxShadow}`,
    },
  };

  const cardActionAreaSx = {
    flex: 1,
    minHeight: 240,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  };

  const cardHeaderSx = {
    height: 175,
    p: 0,
    m: 0,
    backgroundColor: semanticTheme.surface.bg3,
    borderBottom: `0.5px solid ${semanticTheme.outline.light}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const cardContentSx = {
    px: 2,
    '&.MuiCardContent-root:last-child': {
      py: 1.5,
    },
  };

  return (
    <Card sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={onClick} sx={cardActionAreaSx}>
        <CardHeader sx={cardHeaderSx}>{headerContent}</CardHeader>
        <CardContent sx={cardContentSx}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.semantic.text.title2,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.semantic.text.support,
              }}
            >
              {description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
